import * as React from 'react';
import { useEffect, useState } from 'react';

import { omit } from 'lodash';
import { branch, compose, withProps } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import useRouter, { useLocation } from 'js/lib/useRouter';
import useUrlSessionStore from 'js/lib/useUrlSessionStore';
import user from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import EnterpriseSelfServeContractProductPrices from 'bundles/naptimejs/resources/enterpriseSelfServeContractProductPrices.v1';
import { DISCRETIONARY_DISCOUNT_EXPIRED } from 'bundles/teams-v2/components/TeamsCheckoutWarnings';
import type { CheckoutWarning, CheckoutWarnings } from 'bundles/teams-v2/components/TeamsCheckoutWarnings';
import type { CheckoutSessionType } from 'bundles/teams-v2/components/TeamsMainApp';
import {
  getLearnerCountFromRouter,
  getQueryFromLocation,
  getValidDiscretionaryDiscount,
  isDiscretionaryDiscountExpired,
} from 'bundles/teams-v2/components/utils';
import type { ProductPrice } from 'bundles/teams-v2/types/sharedTypes';
import type {
  WithDiscretionaryDiscountProps,
  WithRouterDiscretionaryDiscountCodeProps,
} from 'bundles/teams-v2/utils/withDiscretionaryDiscount';
import {
  useRouterDiscretionaryDiscountCode,
  withDiscretionaryDiscount,
} from 'bundles/teams-v2/utils/withDiscretionaryDiscount';
import type {
  PropsFromPromotionEligibility,
  WithPromoCodeToPromotionProps,
} from 'bundles/teams-v2/utils/withEnterprisePromotion';
import {
  withPromoCodeToPromotion,
  withPromotionEligibility,
  withRouterPromoCode,
} from 'bundles/teams-v2/utils/withEnterprisePromotion';
import type { WithRouterFreeTrialProps } from 'bundles/teams-v2/utils/withFreeTrial';
import { useRouterFreeTrial } from 'bundles/teams-v2/utils/withFreeTrial';

type PropsFromRouter = WithRouterFreeTrialProps &
  WithRouterDiscretionaryDiscountCodeProps & {
    router: InjectedRouter;
    role?: string;
    organizationName?: string;
    organizationType?: string;
    organizationSizeCategory?: string;
    billingTypeStr?: string;
  };

type MappedProps = {
  learnerCount?: number;
  billingType?: number;
};

type PropsFromPriceNaptime = {
  defaultProductPrice?: Array<ProductPrice>;
  productPrice?: Array<ProductPrice>;
};

export type Props = PropsFromRouter &
  MappedProps &
  PropsFromPromotionEligibility &
  WithRouterFreeTrialProps &
  WithPromoCodeToPromotionProps &
  WithRouterDiscretionaryDiscountCodeProps &
  WithDiscretionaryDiscountProps;

type TeamsContextState = {} & WithPromoCodeToPromotionProps &
  WithRouterFreeTrialProps &
  WithRouterDiscretionaryDiscountCodeProps &
  PropsFromPromotionEligibility &
  PropsFromPriceNaptime &
  MappedProps &
  WithDiscretionaryDiscountProps &
  CheckoutWarnings & {
    role?: string;
    organizationName?: string;
    organizationType?: string;
    organizationSizeCategory?: string;
    businessEmail?: string;
    fullName?: string;
  };

export const TeamsContext = React.createContext<TeamsContextState>({});

export const TeamsContainer: React.FC<Props> = (props) => {
  const {
    learnerCount,
    billingType,
    isPromotionEligible,
    promoCode,
    promotion,
    freeTrial,
    enterprisePromotion,
    children,
    discretionaryDiscount,
    discretionaryDiscountCode,
    role,
    organizationName,
    organizationType,
    organizationSizeCategory,
    router,
  } = props;

  // We need DD for router but we want to exclude it from the provider if an incorrect user.
  const validDiscretionaryDiscount = getValidDiscretionaryDiscount(discretionaryDiscount);

  const enterprisePromotionId = isPromotionEligible ? enterprisePromotion?.id : undefined;
  const discretionaryDiscountId = validDiscretionaryDiscount?.id;
  const userId = user.get()?.id ?? 0;

  const {
    data: { productPrice, defaultProductPrice },
  } = useNaptime<PropsFromPriceNaptime>(
    () => ({
      ...(learnerCount && billingType
        ? {
            productPrice: EnterpriseSelfServeContractProductPrices.getEnterpriseSelfServeContractProductPrice({
              userLimit: learnerCount,
              userIdOpt: userId,
              billingType,
              termInstallmentsTotal: billingType,
              enterprisePromotionId,
              discretionaryDiscountId,
            }),
          }
        : {}),
      defaultProductPrice: EnterpriseSelfServeContractProductPrices.getEnterpriseSelfServeContractProductPrice({
        userLimit: 1,
        billingType: 1,
        termInstallmentsTotal: 1,
        enterprisePromotionId,
      }),
    }),
    [learnerCount, billingType, enterprisePromotionId, discretionaryDiscountId, userId]
  );

  const [checkoutWarning, setCheckoutWarning] = useState<CheckoutWarning>(undefined);

  const purchaserUserId = discretionaryDiscount?.purchaserUserId;
  const expiresAt = discretionaryDiscount?.expiresAt;

  const { sessionStore } = useUrlSessionStore<CheckoutSessionType>();

  const getBusinessEmail = () => {
    if (typeof window === 'undefined') {
      return '';
    }

    let email = sessionStore.getState().businessEmail;
    if (!email) {
      if (user.isAuthenticatedUser()) {
        email = user.get().email_address;
      } else {
        email = '';
      }
    }

    return email;
  };
  const businessEmail = getBusinessEmail();

  const getFullName = () => {
    // useUrlSessionStore causes a 500 on SSR without
    if (typeof window === 'undefined') {
      return '';
    }

    let name = sessionStore.getState().fullName;
    if (!name) {
      if (user.isAuthenticatedUser()) {
        name = user.get().full_name;
      } else {
        name = '';
      }
    }

    return name;
  };

  const fullName = getFullName();

  useEffect(() => {
    if (discretionaryDiscountCode && discretionaryDiscount?.id) {
      // If the user ID isn't correct, remove the the DD router param,
      // this should work regardless of page and doesn't cause a reload.
      // If the the DD is expired, do the same.
      const isLoggedOut = !user.isAuthenticatedUser();

      const query = router.location?.query ?? {};
      if (isLoggedOut) {
        router.replace({
          pathname: router.location.pathname,
          query: { ...query, authMode: 'login' },
        });
      } else if (!validDiscretionaryDiscount) {
        router.replace({
          pathname: router.location.pathname,
          query: omit(query, ['dd']),
        });

        if (isDiscretionaryDiscountExpired(discretionaryDiscount)) {
          setCheckoutWarning(DISCRETIONARY_DISCOUNT_EXPIRED);
        }
      }

      if (validDiscretionaryDiscount) {
        router.replace({
          pathname: router.location.pathname,
          query: omit(query, ['promoCode']),
        });
      }
    }
    // router reference isn't dependable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, discretionaryDiscountCode, purchaserUserId, expiresAt]);

  return (
    <TeamsContext.Provider
      value={{
        promotion,
        enterprisePromotion,
        isPromotionEligible,
        promoCode,
        freeTrial,
        defaultProductPrice,
        productPrice,
        learnerCount,
        billingType,
        discretionaryDiscount: validDiscretionaryDiscount,
        checkoutWarning,
        role,
        organizationName,
        organizationType,
        organizationSizeCategory,
        businessEmail,
        fullName,
      }}
    >
      {children}
    </TeamsContext.Provider>
  );
};

const TeamsContainerHOC = compose<Props, PropsFromRouter>(
  withDiscretionaryDiscount,
  // Conditionally find promotion information if not DD
  branch(({ discretionaryDiscount }: WithDiscretionaryDiscountProps) => {
    return !getValidDiscretionaryDiscount(discretionaryDiscount);
  }, compose(withRouterPromoCode, withPromoCodeToPromotion, withPromotionEligibility)),

  withProps<MappedProps, PropsFromRouter & WithDiscretionaryDiscountProps>(
    ({ router, discretionaryDiscount, billingTypeStr }) => {
      const validDiscretionaryDiscount = getValidDiscretionaryDiscount(discretionaryDiscount);
      const learnerCountStr = getLearnerCountFromRouter(router, validDiscretionaryDiscount);

      //  test before parseInt to avoid NaN
      return {
        learnerCount: learnerCountStr ? parseInt(learnerCountStr, 10) : undefined,
        billingType: billingTypeStr ? parseInt(billingTypeStr, 10) : undefined,
      };
    }
  )
)(TeamsContainer);

const TeamsContainerWithRouter: React.FC<{}> = (props: {}) => {
  const { freeTrial } = useRouterFreeTrial();

  // Discretionary Discounts disable promotions
  const { discretionaryDiscountCode } = useRouterDiscretionaryDiscountCode();

  const router = useRouter();
  const location = useLocation();

  // get all query params and session store params
  const role = getQueryFromLocation(location, 'role');
  const organizationName = getQueryFromLocation(location, 'organizationName');
  const organizationType = getQueryFromLocation(location, 'organizationType');
  const organizationSizeCategory = getQueryFromLocation(location, 'organizationSizeCategory');
  const billingTypeStr = getQueryFromLocation(location, 'billingType') ?? '1';

  return (
    <TeamsContainerHOC
      {...props}
      freeTrial={freeTrial}
      discretionaryDiscountCode={discretionaryDiscountCode}
      role={role}
      organizationName={organizationName}
      organizationType={organizationType}
      organizationSizeCategory={organizationSizeCategory}
      billingTypeStr={billingTypeStr}
      router={router}
    />
  );
};

export default TeamsContainerWithRouter;
